
import { useState } from 'react';
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/SafeAuthProvider";
import { JournalFormValues } from '@/components/journal/form/JournalFormSchema';
import { DifficultyRank, getDifficultyConfig } from '@/utils/difficultyRanks';

export const useSaveJournalEntry = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const saveJournalEntry = async (
    values: JournalFormValues, 
    activeDiceValue: number,
    prompts: Record<number, { perception: string; intention: string; nextStep: string }>,
    journalEntryId?: string,
    difficultyRank?: DifficultyRank
  ) => {
    try {
      if (!user) {
        toast({
          title: "Unauthorized",
          description: "You must be logged in to save journal entries.",
          variant: "destructive",
        });
        return false;
      }

      setLoading(true);
      console.log("Saving journal entry with dice value:", activeDiceValue);
      console.log("Values to save:", values);
      console.log("Difficulty rank:", difficultyRank);

      // Get the prompts for the current dice value
      const currentPrompts = prompts[activeDiceValue];

      if (!currentPrompts && activeDiceValue > 0) {
        toast({
          title: "Error",
          description: "Invalid dice value.",
          variant: "destructive",
        });
        return false;
      }

      // Get difficulty configuration
      const difficultyConfig = getDifficultyConfig(difficultyRank);

      const updates = {
        stuck_point: values.stuck_point,
        perception_response: values.perception_response || null,
        intention_response: values.intention_response || null,
        next_step_response: values.next_step_response || null,
        twist_response: values.twist_response || null,
        user_id: user.id,
        dice_value: activeDiceValue,
        perception_prompt: currentPrompts ? currentPrompts.perception : null,
        intention_prompt: currentPrompts ? currentPrompts.intention : null,
        next_step_prompt: currentPrompts ? currentPrompts.nextStep : null,
        twist_prompt: values.twist_response ? "What's the twist?" : null,
        // Set difficulty rank and corresponding progress_max for new entries
        difficulty_rank: journalEntryId ? undefined : difficultyRank,
        progress: journalEntryId ? undefined : 0,
        progress_max: journalEntryId ? undefined : difficultyConfig.pips
      };

      console.log("Saving journal entry:", updates);

      let response;
      if (journalEntryId) {
        // Update existing entry (don't change difficulty or progress_max)
        const { difficulty_rank, progress, progress_max, ...updateData } = updates;
        response = await supabase
          .from("journal_entries")
          .update(updateData)
          .eq("id", journalEntryId);
      } else {
        // Create new entry
        response = await supabase
          .from("journal_entries")
          .insert([{
            ...updates,
            user_id: user.id,
          }]);
      }

      if (response.error) {
        console.error("Error saving journal entry:", response.error);
        toast({
          title: "Error",
          description: "Failed to save journal entry.",
          variant: "destructive",
        });
        return false;
      } else {
        toast({
          title: "Success",
          description: "Journal entry saved successfully.",
        });

        // If it's a new entry and we have data about the inserted entry
        if (!journalEntryId && response.data && response.data.length > 0) {
          // Navigate to the entry page
          navigate(`/journal/${response.data[0].id}`);
          return true;
        } else if (journalEntryId) {
          // Navigate to the entry view page after saving
          navigate(`/journal/${journalEntryId}`);
          return true;
        }
        return true;
      }
    } catch (error) {
      console.error("Unexpected error saving journal entry:", error);
      toast({
        title: "Error",
        description: "Failed to save journal entry.",
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    saveJournalEntry,
    loading
  };
};
